import { useTranslations } from '@vocab/react';
import { Step, Stepper } from 'braid-design-system';

import {
  CAREER_HISTORY,
  EDUCATION,
  FINISH_RESUME,
  LANGUAGES,
  LICENCES,
  PERSONAL_DETAILS,
  SKILLS,
  START_RESUME,
  SUMMARY,
} from 'src/components/Router';
import { useScrollContext } from 'src/components/Router/ScrollContext';
import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import { useAppConfig } from 'src/modules/appConfig';
import type { Card } from 'src/types';

import { useIsValid } from '../../Validation/Validation';
import type { CreateResumeRoute } from '../../types';

import translations from './.vocab';

const mapStepToNumber: Record<string, number> = {
  [START_RESUME]: 0,
  [PERSONAL_DETAILS]: 1,
  [CAREER_HISTORY]: 2,
  [EDUCATION]: 3,
  [LICENCES]: 4,
  [SKILLS]: 5,
  [LANGUAGES]: 6,
  [SUMMARY]: 7,
  [FINISH_RESUME]: 8,
};

const getActiveStep = ({
  step,
  showPersonalSummary,
}: {
  step?: CreateResumeRoute;
  showPersonalSummary: boolean;
}) => {
  if (!step) {
    return 0;
  }
  if (!showPersonalSummary) {
    if (step === FINISH_RESUME) {
      return 7;
    }
  }
  return mapStepToNumber[step];
};

export const ProgressMarkerContainer = () => {
  const { t } = useTranslations(translations);
  const { showFeature } = useAppConfig();
  const showPersonalSummary = showFeature('personal-summary');

  const navigate = useNavigateRoute();
  const scrollContext = useScrollContext();
  const inView = (scrollContext.inView || undefined) as CreateResumeRoute;
  const checkStepValidity = useIsValid();

  const onStepClick = ({ id }: { id?: string | number }) => {
    navigate({
      card: id as Card,
    });
  };

  const activeStep = getActiveStep({ step: inView, showPersonalSummary });

  return showPersonalSummary ? (
    <Stepper
      label="Linear steps"
      mode="non-linear"
      align="left"
      activeStep={activeStep}
      onStepClick={onStepClick}
      data={{ automation: 'create-resume-stepper' }}
    >
      <Step
        id="personal-details"
        complete={checkStepValidity('personal-details')}
      >
        {t('Personal details')}
      </Step>
      <Step id="career-history" complete={checkStepValidity('career-history')}>
        {t('Career history')}
      </Step>
      <Step id="education" complete={checkStepValidity('education')}>
        {t('Education')}
      </Step>
      <Step id="licences" complete={checkStepValidity('licences')}>
        {t('Licences')}
      </Step>
      <Step id="skills" complete={checkStepValidity('skills')}>
        {t('Skills')}
      </Step>
      <Step id="languages" complete={checkStepValidity('languages')}>
        {t('Languages')}
      </Step>

      <Step id="summary" complete={checkStepValidity('summary')}>
        {t('Personal summary')}
      </Step>
      <Step id="finish-resume">{t('Done')}</Step>
    </Stepper>
  ) : (
    <Stepper
      label="Linear steps"
      mode="non-linear"
      activeStep={activeStep}
      onStepClick={onStepClick}
    >
      <Step
        id="personal-details"
        complete={checkStepValidity('personal-details')}
      >
        {t('Personal details')}
      </Step>
      <Step id="career-history" complete={checkStepValidity('career-history')}>
        {t('Career history')}
      </Step>
      <Step id="education" complete={checkStepValidity('education')}>
        {t('Education')}
      </Step>
      <Step id="licences" complete={checkStepValidity('licences')}>
        {t('Licences')}
      </Step>
      <Step id="skills" complete={checkStepValidity('skills')}>
        {t('Skills')}
      </Step>
      <Step id="languages" complete={checkStepValidity('languages')}>
        {t('Languages')}
      </Step>
      <Step id="finish-resume">{t('Done')}</Step>
    </Stepper>
  );
};
